// @avantia common-code
import { SMap } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';

export function applyChanges<ItemT>(existing: ItemT, changes: Partial<ItemT>, moreChanges?: Partial<ItemT>): ItemT {
  return Object.assign({}, existing, changes, moreChanges);
}

export type HpLeafFunction<LeafT> = (leaf: LeafT) => LeafT;

export interface HpApplyInnerChangesOptions {
  copyLeafNode?: boolean;
  copyRootNode?: boolean;
}

export function applyInnerChanges<ItemT, LeafT>(
  item: ItemT,
  childProperties: (string | number)[],
  leaf: LeafT | HpLeafFunction<LeafT>,
  options?: HpApplyInnerChangesOptions
): ItemT {
  const leafIndex = childProperties.length - 1;
  const { copyRootNode = false, copyLeafNode = false } = options || {};
  const root = copyRootNode ? (copyItem<any>(item) as ItemT) : item;
  let node: any = root;

  for (let i = 0; i <= leafIndex; i++) {
    const propName = childProperties[i];
    if (i === leafIndex) {
      if (typeof leaf === 'function') {
        let tmp = node[propName];
        if (copyLeafNode) {
          tmp = copyItem<any>(node[propName]);
        }

        node[propName] = (leaf as HpLeafFunction<LeafT>)(tmp);
      } else {
        node[propName] = leaf;
      }
    } else {
      const parent = node;
      node = parent[propName];
      if (!node) {
        const validProps = typeof parent === 'object' ? lodash.keys(parent) : [];
        const propMessage = validProps.length > 0 ? ` Valid properties are ["${validProps.join('", "')}"].` : '';
        throw new Error(
          `The path traversal for [${childProperties.join('.')}] failed at [${childProperties
            .slice(0, i + 1)
            .join('.')}]. [${propName}] is undefined.${propMessage}`
        );
      }

      node = copyItem<any>(node);
      parent[propName] = node;
    }
  }

  return root;
}

function copyItem<ItemT>(item: any): ItemT[] | SMap<ItemT> {
  if (lodash.isArray(item)) {
    return [...item];
  } else if (typeof item === 'object') {
    return { ...item };
  }

  throw new Error(`Unable to copy items of type '${typeof item}'`);
}
